import React, { ReactElement, useCallback, useMemo } from 'react'
import { Section, SectionContent, SectionItem, SubTitle } from '../../components/Shared'
import { SenderAddress } from '../../state/Radom'
import { ExternalLinkText } from '../Events'
import { formatExplorerUrl } from '../../util/Chains'
import { errorToast, shortAddress, successToast } from '../../util/Util'
import LinkExternal from '../../components/LinkExternal'
import { RADOM_COLORS } from '../../util/Constants'
import { IconButton } from '../../components/Button'
import Copy from '../../icons/Copy'

interface DepositChainDetailsProps {
  network: string
  transactionHash?: string
  senderAddresses?: SenderAddress[]
  amount?: number | string
  ticker?: string
  showEmpty?: boolean
}

const emptyTextStyle: React.CSSProperties = {
  color: RADOM_COLORS.GRAY_DARKER
}

const valueRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  minWidth: 0
}

const copyIconStyle: React.CSSProperties = {
  width: 15,
  stroke: RADOM_COLORS.GRAY_DARKER
}

const DepositChainDetails = ({
  network,
  transactionHash,
  senderAddresses = [],
  amount,
  ticker,
  showEmpty = false
}: DepositChainDetailsProps): ReactElement | null => {
  const hasSenderAddresses = senderAddresses.length > 0
  const formattedAmount = amount == null ? undefined : amount.toString()

  const txUrl = useMemo(() => {
    if (!network || !transactionHash) return ''
    return formatExplorerUrl(network, 'tx', transactionHash)
  }, [network, transactionHash])

  const copyValue = useCallback((label: string, value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => successToast(`Copied ${label}`))
      .catch((err) => {
        console.error('copy failed', err)
        errorToast(`Failed to copy ${label}`)
      })
  }, [])

  const renderCopyButton = (label: string, value: string): ReactElement => (
    <IconButton
      type='button'
      style={{ padding: 0 }}
      onClick={(e) => {
        e.stopPropagation()
        copyValue(label, value)
      }}
    >
      <Copy style={copyIconStyle} />
    </IconButton>
  )

  const renderExternalValue = (type: 'address' | 'tx', value: string): ReactElement => {
    const href = type === 'tx' ? txUrl : formatExplorerUrl(network, 'address', value)

    return <ExternalLinkText
      onClick={e => e.stopPropagation()}
      target='_blank'
      rel='noreferrer'
      href={href}
      style={{
        minWidth: 0,
        overflowWrap: 'anywhere'
      }}
    >
      {shortAddress(value, 18)}
      <LinkExternal stroke={RADOM_COLORS.BLACK} width={13} />
    </ExternalLinkText>
  }

  if (!formattedAmount && !transactionHash && !hasSenderAddresses && !showEmpty) {
    return null
  }

  return <Section>
    <SubTitle style={{ fontSize: 18 }}>
      Blockchain details
    </SubTitle>

    <SectionContent>
      <SectionItem>
        <SubTitle>Amount</SubTitle>
        {
          formattedAmount
            ? <span>{formattedAmount}{ticker ? ` ${ticker}` : ''}</span>
            : <span style={emptyTextStyle}>Not available</span>
        }
      </SectionItem>

      <SectionItem>
        <SubTitle>Transaction hash</SubTitle>
        {
          transactionHash
            ? <div style={valueRowStyle}>
              {renderExternalValue('tx', transactionHash)}
              {renderCopyButton('transaction hash', transactionHash)}
            </div>
            : <span style={emptyTextStyle}>Not available</span>
        }
      </SectionItem>

      <SectionItem>
        <SubTitle>{senderAddresses.length === 1 ? 'Sender address' : 'Sender addresses'}</SubTitle>
        {
          hasSenderAddresses
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {senderAddresses.map((sender, idx) => (
                <div key={`${sender.address}-${idx}`} style={valueRowStyle}>
                  {renderExternalValue('address', sender.address)}
                  {renderCopyButton('sender address', sender.address)}
                </div>
              ))}
            </div>
            : <span style={emptyTextStyle}>Not available</span>
        }
      </SectionItem>
    </SectionContent>
  </Section>
}

export default DepositChainDetails
